import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '../../ui/table';

import { motion, AnimatePresence } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { doGetIncorrectlyFailedOrders } from '../../../modules/orders/store/thunkts';
import { AppDispatch } from '@/redux/store';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { selectIncorrectlyFailedOrders } from '../../../modules/orders/store/slice';
import { Button } from '../../ui/button';
import { SkipBack, SkipForward } from 'lucide-react';

import { format } from 'date-fns';

const IncorrectlyFailed = () => {
  const dispatch = useDispatch<AppDispatch>();
  const history = useHistory();
  const [page, setPage] = useState<number>(1);
  const incorrectlyFailedOrders = useSelector(selectIncorrectlyFailedOrders);

  const nextPage = () => {
    if (page * 10 < incorrectlyFailedOrders.length) {
      setPage(page + 1);
    }
  };

  const prevPage = () => {
    if ((page - 1) * 10 > 0) {
      setPage(page - 1);
    }
  };

  const { from, to } = useMemo(
    () => ({ from: (page - 1) * 10 + 1, to: Math.min(incorrectlyFailedOrders.length, page * 10) }),
    [page, incorrectlyFailedOrders],
  );

  useEffect(() => {
    dispatch(doGetIncorrectlyFailedOrders());
  }, [dispatch]);

  const sortedInactiveOrders = useMemo(
    () =>
      [...incorrectlyFailedOrders]
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
        .slice(from - 1, to), // -1 to match 0 index
    [incorrectlyFailedOrders, from, to],
  );

  const handleRowClick = (orderId: string, e: React.MouseEvent) => {
    const directTo = `/orders/${orderId}`;
    if (e.ctrlKey || e.metaKey) {
      window.open(directTo, '_blank');
    } else {
      history.push(directTo);
    }
  };

  return (
    <Card className="min-h-[400px]">
      <CardHeader>
        <CardTitle>Noncompliant failed attempts</CardTitle>
        <CardDescription>
          This overview lists orders with delivery attempts outside agreed delivery time window. Please schedule another
          attempt. Orders will be resolved once we automatically receive webhook updates indicating this.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              {/* <TableHead>Order ID</TableHead> */}
              <TableHead colSpan={2}>Barcode</TableHead>
              <TableHead>Addressee</TableHead>
              <TableHead className="text-right">Noncompliant attempt</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {incorrectlyFailedOrders?.length === 0 ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={4} className="text-center text-gray-500 pt-12">
                  No noncompliant failed attempts that need your attention, enjoy your day!
                </TableCell>
              </TableRow>
            ) : (
              <AnimatePresence mode="popLayout">
                {sortedInactiveOrders.map((order) => (
                  <motion.tr
                    key={order.orderId}
                    initial={{ opacity: 1, height: 'auto' }}
                    exit={{
                      opacity: 0,
                      height: 0,
                      transition: {
                        duration: 0.2,
                        ease: 'easeOut',
                      },
                    }}
                    onClick={(e) => handleRowClick(order.orderId, e)}
                    className="hover:cursor-pointer hover:bg-gray-50"
                    style={{ display: 'table-row' }}
                    layout
                  >
                    {/* <TableCell className="font-medium overflow-hidden text-ellipsis whitespace-nowrap">
                      <div>{order.orderId}</div>
                    </TableCell> */}
                    <TableCell colSpan={2} className=" overflow-hidden text-ellipsis whitespace-nowrap">
                      <div className="font-semibold">
                        {order.barcode}
                        {/* {order.colli?.length > 1 && (
                          <>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <Badge variant="outline" className="ml-1">
                                    {'+' + (order.colli.length - 1).toString()}
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {order.colli.map((collo) => (
                                    <div key={collo.barcode}>{collo.barcode}</div>
                                  ))}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </>
                        )} */}
                      </div>
                    </TableCell>
                    <TableCell
                      title={order.deliveryAddress}
                      className=" overflow-hidden text-ellipsis whitespace-nowrap"
                    >
                      <div>{order.deliveryName}</div>
                    </TableCell>
                    <TableCell className="text-right overflow-hidden text-ellipsis whitespace-nowrap">
                      <div>{format(order.logs[0].createdAt, 'EEE dd-MM HH:mm')}</div>
                    </TableCell>
                  </motion.tr>
                ))}
              </AnimatePresence>
            )}
          </TableBody>
          {incorrectlyFailedOrders?.length > 0 && (
            <TableFooter>
              <TableRow>
                <TableCell>
                  <Button variant="outline" size="sm" className="mr-1" onClick={prevPage} disabled={page === 1}>
                    <SkipBack />
                  </Button>
                </TableCell>
                <TableCell colSpan={2}>
                  Showing {from} to {to} of {incorrectlyFailedOrders.length} orders
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="outline"
                    size="sm"
                    className="mr-1"
                    onClick={nextPage}
                    disabled={page * 10 >= incorrectlyFailedOrders.length}
                  >
                    <SkipForward />
                  </Button>
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </CardContent>
    </Card>
  );
};

export default IncorrectlyFailed;
